import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from '../hooks/useFormatMoney';
import type { SubTradeRequest } from '../types';
import { isBuyType } from '../utils';
export type PortfolioRowDetailPendingTradesSummaryProps = {
  pendingTrades: SubTradeRequest[];
  theme: Theme;
};

export const PortfolioRowDetailPendingTradesSummary = ({
  pendingTrades,
  theme,
}: PortfolioRowDetailPendingTradesSummaryProps) => {
  const { t } = useTranslation('portfolioTradingHub');
  const formatMoney = useFormatMoney();

  if (pendingTrades.length === 0) return null;

  const totalBuys = pendingTrades.filter((trade) => isBuyType(trade.type));
  const totalSells = pendingTrades.filter((trade) => !isBuyType(trade.type));
  const buyTotal = totalBuys.reduce(
    (sum, trade) => sum + Math.abs(trade.moneyAllocatedCents || 0),
    0,
  );
  const sellTotal = totalSells.reduce(
    (sum, trade) => sum + Math.abs(trade.moneyAllocatedCents || 0),
    0,
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        px: 1.5,
        py: 0.75,
        bgcolor: theme.color.neutralHover,
        borderTop: '1px solid',
        borderColor: theme.color.outlineVariant,
      }}
    >
      <Typography
        variant='labelSmall'
        sx={{ fontWeight: 700, color: theme.color.positive }}
      >
        {t('rowDetail.pendingTrades.buyCount', { count: totalBuys.length })} ·{' '}
        {formatMoney(buyTotal)}
      </Typography>
      <Typography
        variant='labelSmall'
        sx={{ fontWeight: 700, color: theme.color.negative }}
      >
        {t('rowDetail.pendingTrades.sellCount', { count: totalSells.length })} ·{' '}
        {formatMoney(sellTotal)}
      </Typography>
      <Typography
        variant='labelSmall'
        sx={{
          fontWeight: 700,
          color: theme.color.onSurfaceVariant,
          ml: 'auto',
        }}
      >
        {t('rowDetail.pendingTrades.net', {
          value: formatMoney(buyTotal - sellTotal),
        })}
      </Typography>
    </Box>
  );
};
